/**
 * The editing session for one image: boxes, cursor, absences, autosave, and the lock.
 *
 * Coordinates
 * -----------
 * Every `bbox` in this store is **image pixel space**, floats, produced by `coords.ts`. No
 * arithmetic on a screen coordinate happens here or anywhere above here — the canvas converts
 * once and hands over image pixels. The undo stack and the autosave payload are in the same
 * space by construction.
 *
 * Autosave
 * --------
 * Debounced bulk upsert of the **whole** box set (`POST /api/images/{id}/annotations`). Never
 * N patches: a client-computed diff is a diff nobody can audit, and N requests are N chances
 * to be half-applied when the network drops mid-inscription.
 *
 * Failure is a **state**, not a toast. A failed save keeps the work in memory, keeps it in
 * localStorage, keeps `dirty` true, and retries with backoff behind a banner that stays up
 * until it succeeds. The one thing it does not do is fight another annotator: a 409
 * `image_locked` stops the loop dead and says who holds the image.
 */

import { api } from '../api/client';
import { asProblem, type ApiProblem } from '../api/problem';
import type { OrientedRect, Size } from '../coords';
import { clampRectToImage, cornersOutside, normalizeAngle } from '../coords';
import {
  assignmentForCursor,
  boxFromAnnotation,
  boxFromAssignment,
  clearedScholarFlags,
  cursorAfterDraw,
  isLacunaToken,
  nextUnresolved,
  relabelBox,
  resolvedTokenIndices,
  setBoxClass,
  stepCursor,
  chiralityCycleRefusal,
  mirroredForChirality,
  nextChirality,
  type DraftBox,
} from '../draft';
import { letterChirality } from '../glyphs';
import { imageProgress } from '../progress';
import { annotator } from './annotator.svelte';
import { app } from './app.svelte';
import {
  cloneBoxes,
  clearDraft,
  draftOwnership,
  isDraftStale,
  loadDraft,
  saveDraft,
  type StoredDraft,
} from './persist';
import type {
  AnnotationDraftIn,
  BulkUpsertRequest,
  Chirality,
  ImageDetail,
  ImageStatus,
  InscriptionDetail,
  InscriptionPatch,
  InscriptionResetSummary,
  LayoutType,
  ReadingDirection,
  RejectedBoxOut,
  TokenOut,
  VocabEntryOut,
  WriteAnomalyOut,
} from '../api/types';

const AUTOSAVE_DELAY_MS = 900;
const DRAFT_DELAY_MS = 400;
const MAX_UNDO = 60;
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 30000;

export type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'retrying' | 'failed' | 'blocked';

interface Snapshot {
  boxes: DraftBox[];
  absent: number[];
  cursor: number;
}

export interface LockLoss {
  holder: string;
  expiresAt: string | null;
  message: string;
}

class EditorSession {
  // --- identity of what is open -------------------------------------------------
  imageId = $state<number | null>(null);
  image = $state<ImageDetail | null>(null);
  inscription = $state<InscriptionDetail | null>(null);
  imageUrl = $state<string | null>(null);
  /** Why imagery is unavailable, when it is. Annotation without a picture is not possible. */
  imageProblem = $state<ApiProblem | null>(null);
  loading = $state(false);
  loadProblem = $state<ApiProblem | null>(null);

  // --- the work -----------------------------------------------------------------
  boxes = $state<DraftBox[]>([]);
  absent = $state<number[]>([]);
  cursor = $state(0);
  selectedKey = $state<string | null>(null);
  /**
   * The inscription's, not this image's — see `#saveAttributes`. Mirrored into the session so
   * the chip strip and the palette can be laid out and turned without reaching through
   * `inscription` on every render; `inscription` remains the authority and a failed write
   * restores from it.
   *
   * **Nothing a box stores is derived from it.** It used to default every box's `mirrored`
   * flag through `suggestMirrored`, which is exactly what `chirality` exists to end.
   */
  readingDirection = $state<ReadingDirection | null>(null);
  readingDirectionAngle = $state<number | null>(null);
  layoutType = $state<LayoutType | null>(null);
  /** An inscription-attribute write is in flight. */
  attributeSaving = $state(false);
  /** Why the last direction/layout write failed. Cleared when the next one is attempted. */
  attributeProblem = $state<ApiProblem | null>(null);
  /** Armed by the "insert unexpected letter" shortcut; consumed by the next box drawn. */
  insertMode = $state(false);
  drawMode = $state(true);

  /** Natural pixel size of the loaded image. Recorded with the boxes so every consumer
   * normalizes against the dimensions in force at annotation time. */
  naturalSize = $state<Size | null>(null);

  // --- save machinery -----------------------------------------------------------
  saveState = $state<SaveState>('clean');
  saveProblem = $state<ApiProblem | null>(null);
  lockLoss = $state<LockLoss | null>(null);
  readOnly = $state(false);
  retryAttempt = $state(0);
  lastSavedAt = $state<string | null>(null);
  rejected = $state<RejectedBoxOut[]>([]);
  anomalies = $state<WriteAnomalyOut[]>([]);
  /** Transient explanations for the last action — e.g. "recorded as an unexpected letter". */
  notices = $state<string[]>([]);
  recoverable = $state<StoredDraft | null>(null);
  /** Whose the offered draft is, relative to whoever is at the keyboard now. */
  recoverableOwner = $state<'mine' | 'other' | 'unattributed'>('unattributed');
  /**
   * The offered draft is older than {@link DRAFT_MAX_AGE_MS}, or carries a timestamp that
   * cannot be read at all. It is offered anyway, with the warning — see `#offerDraft`.
   */
  recoverableStale = $state(false);
  /**
   * Set when work had to be abandoned in memory and could not be written to localStorage
   * either. The workspace refuses to load over the top of it — see `#reset`.
   */
  strandedWork = $state(false);

  #undo: Snapshot[] = [];
  #redo: Snapshot[] = [];
  /** Serializes direction/layout writes; see `#saveAttributes`. */
  #attributeWrite: Promise<void> = Promise.resolve();
  #attributePending = 0;
  #saveTimer: ReturnType<typeof setTimeout> | null = null;
  #draftTimer: ReturnType<typeof setTimeout> | null = null;
  #retryTimer: ReturnType<typeof setTimeout> | null = null;
  #revision = 0;
  /**
   * The outstanding bulk upsert, or null. A boolean flag was not enough: a caller that needs
   * the save to have *landed* (marking an image done, closing the image) has to be able to
   * wait for it, and an early `return` looks identical to "saved" from the outside.
   */
  #inFlightSave: Promise<void> | null = null;
  /**
   * A bulk upsert left over from a **previous** session that has not landed yet.
   *
   * The next save waits for it before issuing. The submitted list is authoritative, so two
   * requests for the same image landing out of order deletes whatever the later-sent one did
   * not name — and `#reset()` detaching `#inFlightSave` is exactly what made two concurrent
   * requests for the same image reachable (open A, save, navigate to B, navigate back to A).
   */
  #draining: Promise<void> | null = null;
  /**
   * Bumped by every `#reset()`. This — not `imageId` — is the identity a response is checked
   * against: `imageId` is not a session identity, so navigating A -> B -> A inside one in-flight
   * request passed an `imageId` guard and let a stale response stamp its ids, progress and lock
   * state onto a session that had already thrown that state away.
   */
  #generation = 0;
  /**
   * Annotation ids the service is known to hold for this image. A draft restored from
   * localStorage may name rows that have since been deleted; sending one is an
   * `invariant_violation`, which is not retryable, which used to brick saving permanently.
   */
  #serverIds = new Set<number>();
  /**
   * Which image `open()` is already working on. A plain field, deliberately **not** `$state`:
   * the guard must not be a reactive read, or an effect that calls `open()` re-runs when
   * `open()` clears it and starts the load again.
   */
  #openedId: number | null = null;
  /**
   * Which `open()` call owns the session, bumped on entry to every one of them.
   *
   * The save path has been guarded against interleaving since `#generation` existed; the
   * **load** path was not. `#openedId` only stops an effect re-entering `open()` for the *same*
   * image, so `open(A)` then `open(B)` put two loads in the air at once, and each one resumed
   * from its awaits — `imageAnnotations`, `inscriptionById`, `imageUrl`, `setImageStatus` — and
   * stamped its result over whatever was there. Losing the race by a few milliseconds gave the
   * annotator image B's photograph with image A's boxes, A's expected-letter chips and A's
   * lock, all of it looking entirely ordinary. Every box drawn after that is coordinates from
   * one stone stored against another.
   *
   * `#generation` is not usable for this: `#reset()` bumps it, and the two `open()` calls reach
   * `#reset()` in whatever order their `close()` promises happen to settle, so the *earlier*
   * call can hold the newer generation. The token is taken before any await, so the last
   * `open()` to be *called* is the one that owns the session — which is the one the router
   * asked for.
   */
  #loadToken = 0;

  // --- derived ------------------------------------------------------------------

  tokens = $derived<TokenOut[]>(this.inscription?.tokens ?? []);
  absentSet = $derived(new Set(this.absent));
  resolved = $derived(resolvedTokenIndices(this.boxes, this.absentSet));
  progress = $derived(imageProgress(this.tokens.length, this.boxes, this.absentSet));
  currentToken = $derived<TokenOut | undefined>(this.tokens[this.cursor]);
  selected = $derived<DraftBox | undefined>(
    this.boxes.find((box) => box.key === this.selectedKey),
  );
  canDraw = $derived(!this.readOnly && this.imageUrl !== null && annotator.ready);

  /** What a box drawn right now would become. Rendered next to the cursor so the annotator
   * can see the auto-assignment before committing to it. */
  pendingAssignment = $derived(
    assignmentForCursor({
      tokens: this.tokens,
      boxes: this.boxes,
      cursor: this.cursor,
      forceUnexpected: this.insertMode,
      fallbackClass: app.unknownClass,
    }),
  );

  // ------------------------------------------------------------------------------
  // Loading
  // ------------------------------------------------------------------------------

  async open(imageId: number): Promise<void> {
    if (this.#openedId === imageId) return;
    this.#openedId = imageId;
    // Taken before the first await, so "who owns the session" is decided by call order rather
    // than by which promise settles first. See `#loadToken`.
    const token = (this.#loadToken += 1);
    await this.close();
    // Superseded while the previous image was being flushed: this call has mutated nothing yet
    // and must not start, or two loads run against one session again.
    if (token !== this.#loadToken) return;
    if (!this.#reset()) {
      // Unsaved boxes that reached neither the service nor localStorage. Opening another image
      // over the top of them is the one move that turns "not saved yet" into "gone", so it is
      // refused and the work stays on screen where it can still be copied out.
      //
      // Released so the refusal is recoverable: the guard at the top of `open()` is there to
      // stop an effect re-entering a load in progress, not to make a *failed* load permanent.
      // Leaving it set meant re-navigating to this image short-circuited and the banner stayed
      // up until the annotator reloaded the page.
      this.#openedId = null;
      this.loading = false;
      this.loadProblem = asProblem(
        new Error(
          'There are unsaved boxes for the previous image that could not be saved or stored in this browser. Copy them out from the save banner before opening another image.',
        ),
      );
      return;
    }
    this.imageId = imageId;
    this.loading = true;
    try {
      const state = await api.imageAnnotations(imageId);
      if (token !== this.#loadToken) return;
      this.image = state.image;
      this.boxes = state.annotations.map(boxFromAnnotation);
      this.#serverIds = new Set(state.annotations.map((row) => row.id));
      this.absent = [...state.absent_token_indices];
      if (state.image.width && state.image.height) {
        this.naturalSize = { width: state.image.width, height: state.image.height };
      }

      if (state.image.inscription_id !== null) {
        const inscription = await api.inscriptionById(state.image.inscription_id);
        if (token !== this.#loadToken) return;
        this.inscription = inscription;
        // From the record, not the photograph: the direction is a fact about the writing and
        // one answer covers every image of the stone. An image with no linked record leaves
        // these null and the panel says why — there is nowhere for the answer to live.
        this.#applyAttributes(inscription);
      }
      this.cursor = nextUnresolved({ tokens: this.tokens, resolved: this.resolved, cursor: -1 });

      await this.#loadImageUrl(imageId, token);
      if (token !== this.#loadToken) return;
      await this.#claim(state.image, token);
      if (token !== this.#loadToken) return;

      this.#offerDraft(imageId);
    } catch (error) {
      if (token !== this.#loadToken) return;
      this.loadProblem = asProblem(error);
      // Same reasoning as the refusal above: a load that failed must be retryable by
      // navigating to the image again, not only by reloading the page.
      this.#openedId = null;
    } finally {
      // Only the owning load may clear the spinner. A superseded one doing it uncovers a
      // half-loaded workspace belonging to neither image.
      if (token === this.#loadToken) this.loading = false;
    }
  }

  async #loadImageUrl(imageId: number, token: number): Promise<void> {
    try {
      const presigned = await api.imageUrl(imageId);
      if (token !== this.#loadToken) return;
      // Deliberately not logged, not stored, not put in the URL bar. Short-TTL signed URLs
      // belong in an <img> src and nowhere else.
      this.imageUrl = presigned.url;
      if (presigned.width && presigned.height) {
        this.naturalSize = { width: presigned.width, height: presigned.height };
      }
    } catch (error) {
      if (token !== this.#loadToken) return;
      this.imageProblem = asProblem(error);
      this.imageUrl = null;
    }
  }

  /**
   * Take the single-writer lock.
   *
   * There is no bare "acquire lock" endpoint, and that is fine: claiming an image *is* moving
   * it to `in_progress`, which is exactly what the annotator is doing. A `done` image being
   * reopened keeps its status so reopening it to fix one box does not regress the queue.
   *
   * There is no steal flag to pass. A stale lock is reclaimed by this call with no ceremony; a
   * live one is a 409 and stays a 409. The client's only move is to try again later.
   */
  async #claim(image: ImageDetail, token: number = this.#loadToken): Promise<void> {
    if (!annotator.ready) {
      this.readOnly = true;
      // Say so, and ask. An unset id cannot claim a lock, so the image would otherwise open
      // read-only with the interface blaming a lock nobody holds — which is exactly the
      // confusion this branch used to cause.
      annotator.requestIdentity('open-image');
      return;
    }
    const target: ImageStatus = image.status === 'done' ? 'done' : 'in_progress';
    try {
      const claimed = await api.setImageStatus(image.id, {
        status: target,
        release_lock: false,
      });
      // A claim that lands after the annotator has moved on belongs to the image they left. It
      // is a real lock and the service holds it, but stamping it here would tell a session for
      // another image that it holds a lock on someone else's.
      if (token !== this.#loadToken) return;
      this.image = claimed;
      this.readOnly = false;
      this.lockLoss = null;
    } catch (error) {
      if (token !== this.#loadToken) return;
      const problem = asProblem(error);
      if (problem.code === 'image_locked') {
        this.#enterLockLoss(problem);
      } else {
        this.saveProblem = problem;
        this.saveState = 'failed';
        this.readOnly = true;
      }
    }
  }

  /**
   * Try to claim the image again.
   *
   * Succeeds once the other annotator's lock has gone stale, and fails with the same 409 until
   * then. Deliberately a manual button rather than a poll: quietly taking an image the moment
   * a colleague's TTL lapses, while they are still looking at it, is how two people end up
   * overwriting each other with a mechanism that was supposed to stop exactly that.
   */
  async retryClaim(): Promise<void> {
    if (!this.image) return;
    await this.#claim(this.image);
    if (!this.lockLoss) {
      this.saveState = this.#dirty ? 'dirty' : 'clean';
      this.#scheduleSave();
    }
  }

  #enterLockLoss(problem: ApiProblem): void {
    this.readOnly = true;
    this.saveState = 'blocked';
    this.saveProblem = problem;
    this.lockLoss = {
      holder: problem.lockHolder ?? 'another annotator',
      expiresAt: problem.lockExpiresAt,
      message: problem.message,
    };
    this.#cancelTimers();
  }

  // ------------------------------------------------------------------------------
  // Draft recovery
  // ------------------------------------------------------------------------------

  /**
   * Decide whether a stored draft is worth offering, and say what is doubtful about it.
   *
   * Nothing here deletes. An earlier version dropped a draft older than a fortnight on sight,
   * leaving only a `notices` entry that the next cursor move wiped — and `draftAgeMs` treated
   * an unparseable `savedAt` as infinitely old, so a single corrupt timestamp destroyed a live
   * draft with no trace at all. Against "never lose work on a failed save" that is the wrong
   * trade in both directions: an old draft the annotator can see and discard costs them one
   * click, and a deleted one costs them the work. So a stale or unreadable draft is offered
   * with the warning attached (`recoverableStale`) and is removed only by
   * `discardRecoverable()` or by a successful save.
   *
   * Two other things are checked: the draft's `annotatorId` (a shared browser must not hand
   * annotator A's boxes to annotator B, who would then save them under their own id — a
   * provenance smear that no export can unpick), and whether the draft says anything at all.
   */
  #offerDraft(imageId: number): void {
    const stored = loadDraft(imageId);
    if (!stored) return;
    // An empty draft over an image the service *does* hold rows for is a deletion, and a
    // deletion is work: `#hasUnsavedWork` no longer treats an emptied image as nothing to
    // save, so this must not treat the resulting draft as nothing to offer.
    const meaningful =
      stored.boxes.length > 0 || stored.absent.length > 0 || this.#serverIds.size > 0;
    if (!meaningful) return;
    this.recoverable = stored;
    this.recoverableOwner = draftOwnership(stored, annotator.id);
    this.recoverableStale = isDraftStale(stored);
  }

  /**
   * Adopt a stored draft.
   *
   * The ids are the dangerous part. A draft carries the `id` of every box that had already
   * been saved when it was written; if any of those rows have since been deleted server-side,
   * the next bulk upsert is a 409 `invariant_violation` — not retryable, so the save state
   * went to `failed` permanently with nothing in the UI able to identify the offending box.
   * So every restored id is intersected with the ids the service just told us it holds, and
   * anything else is nulled and re-created as a new row. Losing an id costs a new row; keeping
   * a stale one costs the whole session.
   *
   * **Another annotator's draft is refused, not warned about.** `annotator_id` is sent per
   * request, so restoring A's boxes while B is at the keyboard writes them to the database as
   * B's — and provenance is non-nullable precisely so that "who drew this box" survives into
   * every export and every later question about whether the model's errors track its training
   * data. A warning does not preserve that; it only records that someone was told before it was
   * destroyed. Nothing is deleted either: the draft stays in this browser for its owner, and
   * `recoverableJson()` gets it out by hand if they are not coming back.
   */
  restoreRecoverable(): boolean {
    const stored = this.recoverable;
    if (!stored) return false;
    if (this.recoverableOwner === 'other') {
      this.notices = [
        `These boxes were drafted by ${stored.annotatorId}. They cannot be restored under ${annotator.id || 'another id'}: every box carries the annotator who drew it, and saving them here would attribute ${stored.annotatorId}'s work to you permanently. Set this session's annotator id to ${stored.annotatorId} to recover them, or copy the draft out and hand it back.`,
      ];
      return false;
    }
    this.#pushUndo();
    let orphaned = 0;
    this.boxes = cloneBoxes(stored.boxes).map((box) => {
      if (box.id === null || this.#serverIds.has(box.id)) return box;
      orphaned += 1;
      return { ...box, id: null };
    });
    this.absent = [...stored.absent];
    // The draft's direction and layout are deliberately *not* restored. They are the
    // record's, they are written immediately rather than autosaved, so whatever the service
    // holds is already the later statement — and restoring a fortnight-old draft's answer
    // over it would silently change a fact about a stone the annotator is not looking at.
    // They stay in the draft, and `recoverableJson()` still carries them out.

    const notes: string[] = [];
    if (this.recoverableStale) {
      notes.push(
        `This draft was written on ${new Date(stored.savedAt).toLocaleString()}, which is either older than a fortnight or a timestamp this browser cannot read. Check the boxes against the stone before completing the image.`,
      );
    }
    if (this.recoverableOwner === 'unattributed') {
      // Not the same case as another annotator's draft. There is no provenance here to smear —
      // the draft predates any id being set — so it is restorable, and the boxes become the
      // work of whoever is now at the keyboard. Which is true, and is worth saying.
      notes.push(
        `This draft carried no annotator id, so its boxes will be saved as ${annotator.id || 'the id you set'}'s.`,
      );
    }
    if (orphaned) {
      notes.push(
        `${orphaned} restored box(es) referred to annotation rows the service no longer has. They were kept and will be saved as new boxes.`,
      );
    }
    this.recoverable = null;
    this.recoverableOwner = 'unattributed';
    this.recoverableStale = false;
    this.#touch();
    this.notices = notes;
    return true;
  }

  /**
   * The offered draft, verbatim, as JSON.
   *
   * The way out for a draft this session is not allowed to restore. It keeps the original
   * `annotatorId` in the payload, so what leaves the browser still says whose work it is —
   * which is the whole point of refusing the restore in the first place.
   */
  recoverableJson(): string {
    const stored = this.recoverable;
    if (!stored) return '{}';
    return JSON.stringify(
      {
        image_id: stored.imageId,
        siglum: this.image?.siglum ?? null,
        storage_key: this.image?.storage_key ?? null,
        drafted_by: stored.annotatorId || null,
        drafted_at: stored.savedAt,
        exported_by: annotator.id || null,
        exported_at: new Date().toISOString(),
        coordinate_space: 'image pixels, origin top-left, floats; bbox.angle is degrees clockwise about the box centre, and x/y/w/h describe the rectangle at angle zero',
        width: this.naturalSize?.width ?? null,
        height: this.naturalSize?.height ?? null,
        boxes: stored.boxes,
        absent_token_indices: stored.absent,
        reading_direction: stored.readingDirection,
        reading_direction_angle: stored.readingDirectionAngle,
        layout_type: stored.layoutType,
      },
      null,
      2,
    );
  }

  discardRecoverable(): void {
    if (this.imageId !== null) clearDraft(this.imageId);
    this.recoverable = null;
    this.recoverableOwner = 'unattributed';
    this.recoverableStale = false;
  }

  // ------------------------------------------------------------------------------
  // Identity changes
  //
  // `annotator_id` is sent per *request*, not per box, so boxes drawn under one identity and
  // saved under another are attributed to whoever happened to be set when the save landed.
  // The dialog warns about that; these three members are what it needs to warn accurately and
  // to limit the damage when the annotator goes ahead anyway.
  // ------------------------------------------------------------------------------

  /** True when this session holds work the service is not yet known to have. */
  get hasUnsavedWork(): boolean {
    return this.#hasUnsavedWork;
  }

  /**
   * Boxes with no server id: rows that do not exist yet and will be *created* under whichever
   * identity is set when the next save lands. A box that already exists keeps the
   * `annotator_id` it was created with, so it is not at risk in the same way.
   */
  get unattributedBoxCount(): number {
    return this.boxes.filter((box) => box.id === null).length;
  }

  /**
   * Boxes on this image whose handedness nobody has recorded and which have one to record.
   *
   * A **readout for the inspector, never a meter** (D9). No progress figure counts chirality
   * and `image_progress` must not grow a term for it: the picker is offered, never prompted,
   * and a bar that fills only when every box has been judged turns "record it when you can
   * plainly see it" into "a nag you have to clear".
   *
   * Two populations are *not* outstanding work, for the same reason: there is no question to
   * answer. A box born `symmetric` has its answer from the measured table, and a box on a
   * class with no reference form (`illegible`, `unknown`) has no frame for `reference` or
   * `mirrored` to name. Counting either would produce a number that can never reach zero.
   *
   * A plain getter, deliberately not the `progress.ts` shape: that module exists because
   * `imageProgress` mirrors a *server* function whose number replaces the client's after every
   * save. There is no server-side unjudged count and there must not be one.
   */
  get unjudgedChiralityCount(): number {
    return this.boxes.filter(
      (box) => box.chirality === null && letterChirality(box.letterClass) === 'chiral',
    ).length;
  }

  /**
   * Rewrite the localStorage draft attributed to `annotatorId` rather than to whoever is at
   * the keyboard now.
   *
   * Called just before the identity changes underneath an open image. Without it the next
   * `#touch()` rewrites the draft under the *new* id, and `draftOwnership` — the check that
   * stops annotator B silently saving A's boxes as their own — has nothing left to compare.
   * Losing that is worse than losing the draft: it turns a visible warning into a silent
   * re-attribution.
   */
  retainDraftFor(annotatorId: string): boolean {
    return this.#writeDraft(annotatorId);
  }

  /**
   * Record on screen that the identity changed while work was open.
   *
   * Not a substitute for the dialog's warning — it is what remains visible *after* the dialog
   * closes, so the fact does not disappear the moment the operator clicks through it.
   */
  noteIdentityChange(previousId: string, nextId: string): void {
    if (this.imageId === null) return;
    const pending = this.unattributedBoxCount;
    this.notices = [
      `The annotator identity changed from ${previousId || 'unset'} to ${nextId} while this image was open.` +
        (pending
          ? ` ${pending} box(es) that have not reached the service yet will be saved as ${nextId}.`
          : ' Boxes already saved keep the id they were created with.'),
    ];
  }

  // ------------------------------------------------------------------------------
  // Mutations. Every one goes through #touch(), which is what arms autosave.
  // ------------------------------------------------------------------------------

  /** The core gesture: a box is drawn, the current expected letter is assigned, cursor moves. */
  addBox(rectImagePixels: OrientedRect): DraftBox | null {
    if (this.readOnly) return null;
    const { rect, notice: geometryNotice } = this.#clamp(rectImagePixels);
    if (!rect) {
      if (geometryNotice) this.notices = [geometryNotice];
      return null;
    }

    const assignment = this.pendingAssignment;
    const box = boxFromAssignment(assignment, rect);
    this.#pushUndo();
    this.boxes = [...this.boxes, box];
    // Selecting the new box is what lets U / M / R / Del act on it with no further gesture.
    // It is only safe because a plain chip click no longer relabels the selection — see
    // `relabelSelected`. Do not re-wire chip clicks to relabel without undoing this.
    this.selectedKey = box.key;

    // Boxing a token that was recorded as not-present is a change of mind; clear the absence
    // rather than letting the two contradict (the API refuses the pair outright).
    if (box.expectedTokenIndex !== null) this.#removeAbsent(box.expectedTokenIndex);

    // Both, in that order: what happened to the geometry, then what happened to the label.
    // Assigning only the assignment note here is what used to discard the trim warning.
    this.notices = [geometryNotice, assignment.note].filter((note): note is string =>
      Boolean(note),
    );

    this.insertMode = false;
    this.cursor = cursorAfterDraw({
      tokens: this.tokens,
      resolved: resolvedTokenIndices(this.boxes, this.absentSet),
      cursor: this.cursor,
    });
    this.#touch();
    return box;
  }

  updateBoxGeometry(key: string, rectImagePixels: OrientedRect): void {
    if (this.readOnly) return;
    const { rect, notice } = this.#clamp(rectImagePixels);
    this.notices = notice ? [notice] : [];
    if (!rect) return;
    this.#pushUndo();
    this.boxes = this.boxes.map((box) => (box.key === key ? { ...box, bbox: rect } : box));
    this.#touch();
  }

  /**
   * Turn a box by `delta` degrees about its own centre.
   *
   * For the glyph that does not follow the general tilt of the text — and on a weathered stone
   * there is always one. Turning is rigid, so nothing is clamped and nothing can be lost: the
   * box keeps its size and its centre, and only its bearing changes.
   */
  rotateBox(key: string, delta: number): void {
    if (this.readOnly) return;
    this.#setAngle(key, (box) => box.bbox.angle + delta);
  }

  /** Set a box's absolute angle. `degrees` is on the photograph, not on the screen. */
  setBoxAngle(key: string, degrees: number): void {
    if (this.readOnly) return;
    this.#setAngle(key, () => degrees);
  }

  #setAngle(key: string, next: (box: DraftBox) => number): void {
    const target = this.boxes.find((box) => box.key === key);
    if (!target) return;
    const angle = normalizeAngle(next(target));
    if (angle === target.bbox.angle) return;
    this.#pushUndo();
    this.boxes = this.boxes.map((box) =>
      box.key === key
        ? // 'obb' unconditionally: a box somebody deliberately turned — even back to level — is
          // a considered orientation, not a box from before the canvas could turn.
          { ...box, bbox: { ...box.bbox, angle, kind: 'obb' as const } }
        : box,
    );
    this.#touch();
  }

  patchBox(key: string, patch: Partial<DraftBox>): void {
    if (this.readOnly) return;
    this.#pushUndo();
    this.boxes = this.boxes.map((box) => (box.key === key ? { ...box, ...patch } : box));
    this.#touch();
  }

  /**
   * The two-state flags, and only those.
   *
   * `'mirrored'` was the third member and is deliberately gone: `chirality` has five values,
   * so there is nothing for a *toggle* to mean on it. Narrowing the type is what turned every
   * remaining caller into a compile error rather than a silently wrong one.
   */
  toggleFlag(key: string, flag: 'uncertain' | 'restored'): void {
    const box = this.boxes.find((item) => item.key === key);
    if (!box) return;
    this.patchBox(key, { [flag]: !box[flag] } as Partial<DraftBox>);
  }

  /**
   * Record a box's handedness, or clear it back to "nobody has looked".
   *
   * The deprecated `mirrored` boolean is restated from it here — the one place chirality is
   * set — so the in-memory box, the localStorage draft, the canvas badge and the save payload
   * cannot disagree about it. One-way only; see `mirroredForChirality`.
   */
  setChirality(key: string, value: Chirality | null): void {
    if (this.readOnly) return;
    const box = this.boxes.find((item) => item.key === key);
    if (!box || box.chirality === value) return;
    this.patchBox(key, {
      chirality: value,
      mirrored: mirroredForChirality(value, box.mirrored),
    });
  }

  /**
   * `M`: step to the next chirality, or say why there is nothing to step through.
   *
   * On a symmetric class, and on one with no reference form at all, this **refuses and
   * changes nothing** rather than cycling — see `chiralityCycleRefusal`. The picker still
   * offers every option in both cases, so the escape hatch survives; what is declined is the
   * fast path, not the judgement.
   */
  cycleChirality(key: string): void {
    if (this.readOnly) return;
    const box = this.boxes.find((item) => item.key === key);
    if (!box) return;
    const refusal = chiralityCycleRefusal(box.letterClass);
    if (refusal) {
      this.notices = [refusal];
      return;
    }
    this.setChirality(key, nextChirality(box.chirality));
  }

  deleteBox(key: string): void {
    if (this.readOnly) return;
    this.#pushUndo();
    this.boxes = this.boxes.filter((box) => box.key !== key);
    if (this.selectedKey === key) this.selectedKey = null;
    this.#touch();
  }

  select(key: string | null): void {
    this.selectedKey = key;
    const box = this.boxes.find((item) => item.key === key);
    if (box?.expectedTokenIndex !== null && box?.expectedTokenIndex !== undefined) {
      this.cursor = box.expectedTokenIndex;
    }
  }

  selectAdjacent(delta: number): void {
    if (this.boxes.length === 0) return;
    const index = this.boxes.findIndex((box) => box.key === this.selectedKey);
    const next = index < 0 ? 0 : (index + delta + this.boxes.length) % this.boxes.length;
    this.select(this.boxes[next]?.key ?? null);
  }

  setCursor(index: number): void {
    this.cursor = Math.min(Math.max(index, 0), Math.max(this.tokens.length - 1, 0));
    this.notices = [];
  }

  stepCursorBy(delta: number): void {
    this.cursor = stepCursor(this.tokens.length, this.cursor, delta);
    this.notices = [];
  }

  /**
   * Relabel the selected box onto a chip. **Never triggered by an ordinary chip click.**
   *
   * `addBox` selects the box it just drew — that is what makes `U`/`M`/`Del` act on it without
   * a further gesture — so "a box is selected" is the *normal* state, not a deliberate one.
   * Wiring a plain chip click to relabel therefore meant that the ordinary flow (draw a box,
   * click chip 9 to jump ahead) silently changed the letter identity of the box just drawn and
   * released its claim on chip 3, with no notice when the target chip happened to be free.
   * That is a silent class change on training data.
   *
   * So relabelling is an explicit affordance: `L`, or Shift+click / right-click on a chip.
   * A plain chip click only moves the cursor. `annotator-api-frontend.md` describes "selecting
   * a box + a chip relabels"; that description assumes selection is a deliberate act, which it
   * is not once every draw selects. The intent behind it — relabel without typing — is met.
   *
   * And it always emits a notice. A class change that the annotator cannot see happening is
   * the defect, not the mechanism that caused it this time.
   */
  relabelSelected(tokenIndex: number): boolean {
    const box = this.selected;
    const token = this.tokens[tokenIndex];
    if (this.readOnly) {
      this.notices = ['This image is read-only, so nothing was relabelled.'];
      return false;
    }
    if (!box) {
      this.notices = ['Select a box first: relabelling needs to know which box to move.'];
      return false;
    }
    if (!token) {
      // Not a defensive branch: `nextUnresolved` parks the cursor at `tokens.length` once every
      // chip is resolved (`draft.ts`), so this is what `L` did after the last chip was boxed —
      // nothing at all, silently, contradicting "it always emits a notice".
      this.notices = [
        this.tokens.length === 0
          ? 'This inscription has no expected letters, so there is no chip to relabel onto. Set the class directly with the class picker (C).'
          : 'The cursor is past the last expected letter, so there is no chip under it. Move the cursor onto a chip and press L again, or set the class directly with the class picker (C).',
      ];
      return false;
    }
    const { box: next, note } = relabelBox(box, token, this.boxes);
    this.#pushUndo();
    this.boxes = this.boxes.map((item) => (item.key === box.key ? next : item));
    if (next.expectedTokenIndex !== null) this.#removeAbsent(next.expectedTokenIndex);
    this.cursor = tokenIndex;
    this.#touch();
    this.notices = note ? [note] : [];
    return true;
  }

  /** `L`: relabel the selected box onto whatever chip the cursor is on. */
  relabelSelectedToCursor(): boolean {
    return this.relabelSelected(this.cursor);
  }

  /**
   * Set an explicit vocabulary class on a box, from the class picker.
   *
   * The reason this exists: an insertion could previously only carry a class that appears
   * somewhere in *this* inscription's chip list, because chips were the only class control in
   * the interface. A glyph whose letter OCIANA does not record anywhere in the inscription was
   * therefore unlabellable, which made one of the three disagreement cases unreachable.
   */
  setClass(key: string, entry: VocabEntryOut): void {
    if (this.readOnly) return;
    const box = this.boxes.find((item) => item.key === key);
    if (!box) return;
    const { box: next, note } = setBoxClass(box, entry, this.tokens);
    if (next === box) {
      this.notices = [];
      return;
    }
    this.#pushUndo();
    this.boxes = this.boxes.map((item) => (item.key === key ? next : item));
    this.#touch();
    this.notices = note ? [note] : [];
  }

  /**
   * Record that an expected letter is **not present** on the stone.
   *
   * This is the other half of first-class mismatch handling and it is not a way of skipping
   * work: it is the annotator stating that OCIANA reads a letter here that the image does not
   * show. It counts as resolved, because it is.
   */
  toggleAbsent(tokenIndex: number): void {
    if (this.readOnly) return;
    const token = this.tokens[tokenIndex];
    if (!token) return;
    this.#pushUndo();
    if (this.absentSet.has(tokenIndex)) {
      this.#removeAbsent(tokenIndex);
      this.notices = [];
    } else {
      // The API refuses a token that is both boxed and absent, so the box goes first.
      const owner = this.boxes.find(
        (box) => box.alignment === 'expected' && box.expectedTokenIndex === tokenIndex,
      );
      if (owner) {
        this.boxes = this.boxes.map((box) =>
          box.key === owner.key
            ? {
                ...box,
                alignment: 'unexpected' as const,
                expectedTokenIndex: null,
                // The chip's {} / [] markers go with the chip. This box is no longer that
                // chip, so keeping them would attribute a scholar's uncertainty to a glyph
                // they never marked.
                ...clearedScholarFlags(),
              }
            : box,
        );
        this.notices = [
          `Expected letter ${tokenIndex + 1} was boxed; that box is now recorded as an unexpected letter so the two statements do not contradict.${
            owner.uncertain || owner.restored
              ? " The editor's uncertain/restored marking was cleared from it: those belong to the chip."
              : ''
          }`,
        ];
      } else {
        this.notices = [];
      }
      this.absent = [...this.absent, tokenIndex].sort((a, b) => a - b);
      this.cursor = nextUnresolved({
        tokens: this.tokens,
        resolved: resolvedTokenIndices(this.boxes, new Set(this.absent)),
        cursor: this.cursor,
      });
    }
    this.#touch();
  }

  /**
   * `N`, from the keyboard or the toolbar: mark the chip under the cursor not present.
   *
   * It lives here rather than as `if (session.currentToken) session.toggleAbsent(...)` in the
   * component so that the no-chip case says something. The cursor legitimately parks past the
   * last chip once everything is resolved, and a shortcut that silently does nothing there
   * reads as a broken keyboard.
   */
  markCurrentAbsent(): void {
    const token = this.currentToken;
    if (!token) {
      this.notices = [
        this.tokens.length === 0
          ? 'This inscription has no expected letters, so there is nothing to mark not present.'
          : 'The cursor is past the last expected letter. Move it onto the chip you mean and press N again.',
      ];
      return;
    }
    this.toggleAbsent(token.index);
  }

  // ------------------------------------------------------------------------------
  // Inscription attributes: reading direction and layout
  //
  // These do not go through the autosave. They are the **record's**, not this photograph's,
  // so they are written immediately through `PATCH /api/inscriptions/by-id/{id}` and every
  // other image of the stone has them the moment it is opened. The bulk upsert cannot carry
  // them any more, which also fixes the old asymmetry where clearing one back to "not set"
  // was silently dropped from the payload.
  // ------------------------------------------------------------------------------

  /** Whether this image's record can carry a direction at all. */
  get attributesWritable(): boolean {
    return this.inscription !== null;
  }

  setReadingDirection(direction: ReadingDirection | null, angle: number | null = null): void {
    if (this.readOnly) return;
    this.readingDirection = direction;
    this.readingDirectionAngle = direction === 'angle' ? angle : null;
    void this.#saveAttributes({
      reading_direction: direction,
      reading_direction_angle: direction === 'angle' ? angle : null,
    });
  }

  setLayout(layout: LayoutType | null): void {
    if (this.readOnly) return;
    this.layoutType = layout;
    void this.#saveAttributes({ layout_type: layout });
  }

  /** Copy the record's stored answers into the editable state. */
  #applyAttributes(record: InscriptionDetail): void {
    this.readingDirection = record.reading_direction;
    this.readingDirectionAngle = record.reading_direction_angle;
    this.layoutType = record.layout_type;
  }

  /**
   * Write one attribute change, serialized behind any earlier one.
   *
   * Serialized rather than fired in parallel because the two controls sit next to each other
   * and a fast annotator sets both: two concurrent PATCHes of one row is a last-response-wins
   * race, and the loser is whichever the network happened to reorder.
   *
   * A failure **puts the control back** to what the service holds. A select still showing the
   * value that failed to store is a lie the annotator will go on to act on — and unlike a box,
   * there is no draft here to recover from.
   */
  async #saveAttributes(patch: InscriptionPatch): Promise<void> {
    const record = this.inscription;
    if (!record) return;
    const revision = this.#revision;
    this.attributeProblem = null;
    this.#attributePending += 1;
    this.attributeSaving = true;

    this.#attributeWrite = this.#attributeWrite
      .catch(() => {})
      .then(async () => {
        try {
          const updated = await api.patchInscription(record.id, patch);
          if (revision !== this.#revision) return;
          this.inscription = updated;
          this.#applyAttributes(updated);
        } catch (error) {
          if (revision !== this.#revision) return;
          this.attributeProblem = asProblem(error);
          this.#applyAttributes(record);
        } finally {
          this.#attributePending -= 1;
          if (this.#attributePending <= 0) this.attributeSaving = false;
        }
      });
    return this.#attributeWrite;
  }

  setNaturalSize(size: Size): void {
    if (this.naturalSize?.width === size.width && this.naturalSize?.height === size.height) return;
    this.naturalSize = size;
    // Recording dimensions is itself worth saving: every consumer normalizes against the
    // numbers in force at annotation time.
    if (this.boxes.length) this.#touch();
  }

  armInsert(): void {
    this.insertMode = !this.insertMode;
    this.notices = this.insertMode
      ? ['Insertion armed: the next box is a letter the transliteration does not contain.']
      : [];
  }

  #removeAbsent(tokenIndex: number): void {
    this.absent = this.absent.filter((index) => index !== tokenIndex);
  }

  /**
   * Reconcile a box with the image bounds, mirroring what the service will do to it.
   *
   * The two shapes are treated differently, and must be: an upright box is trimmed, because
   * trimming a rectangle to a rectangle is well-defined and the result is still the largest
   * box inside both. A turned one is **not**, because no trim of it preserves both its angle
   * and the annotator's judgement about which marks belong to the glyph — so it is kept as
   * drawn and the annotator is told, exactly as the service keeps it and records an anomaly.
   * Clamping here while the server preserves would put a box on screen that is not the box in
   * the database.
   */
  /**
   * Returns the box and whatever needs saying about it, rather than *setting* the notice.
   *
   * It used to assign `this.notices` directly, and `addBox` then overwrote that assignment two
   * dozen lines later — so a **drawn** box that had to be trimmed said nothing at all. (A
   * resized one did, because `updateBoxGeometry` has no such assignment. Same clamp, two
   * different behaviours, and only one of them visible.) Handing the notice back makes the
   * caller responsible for keeping it, which is a thing the type system can see.
   */
  #clamp(rect: OrientedRect): { rect: OrientedRect | null; notice: string | null } {
    const size = this.naturalSize;
    if (!size) return { rect, notice: null };
    if (rect.angle !== 0) {
      const notice = cornersOutside(rect, size)
        ? 'That box extends past the image edge. A turned box is stored exactly as drawn ' +
          'rather than trimmed, and the save is recorded as an anomaly.'
        : null;
      return { rect, notice };
    }
    const result = clampRectToImage(rect, size);
    if (result.degenerate) {
      return { rect: null, notice: 'That box lies outside the image and was not stored.' };
    }
    return {
      rect: { ...rect, ...result.rect },
      notice: result.clamped
        ? 'That box extended past the image edge and was trimmed to fit.'
        : null,
    };
  }

  // ------------------------------------------------------------------------------
  // Undo / redo
  // ------------------------------------------------------------------------------

  #snapshot(): Snapshot {
    return { boxes: cloneBoxes(this.boxes), absent: [...this.absent], cursor: this.cursor };
  }

  #pushUndo(): void {
    this.#undo.push(this.#snapshot());
    if (this.#undo.length > MAX_UNDO) this.#undo.shift();
    this.#redo = [];
  }

  get canUndo(): boolean {
    return this.#undo.length > 0;
  }

  get canRedo(): boolean {
    return this.#redo.length > 0;
  }

  undo(): void {
    const previous = this.#undo.pop();
    if (!previous) return;
    this.#redo.push(this.#snapshot());
    this.#apply(previous);
  }

  redo(): void {
    const next = this.#redo.pop();
    if (!next) return;
    this.#undo.push(this.#snapshot());
    this.#apply(next);
  }

  #apply(snapshot: Snapshot): void {
    this.boxes = snapshot.boxes;
    this.absent = snapshot.absent;
    this.cursor = snapshot.cursor;
    this.#touch();
  }

  // ------------------------------------------------------------------------------
  // Autosave
  // ------------------------------------------------------------------------------

  get #dirty(): boolean {
    return this.saveState === 'dirty' || this.saveState === 'retrying' || this.saveState === 'failed';
  }

  #touch(): void {
    this.#revision += 1;
    if (this.saveState !== 'blocked') this.saveState = 'dirty';
    this.#scheduleDraft();
    this.#scheduleSave();
  }

  #scheduleDraft(): void {
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    this.#draftTimer = setTimeout(() => this.#writeDraft(), DRAFT_DELAY_MS);
  }

  /** Write the draft right now, synchronously. Returns whether it reached storage. */
  #writeDraft(annotatorId: string = annotator.id): boolean {
    if (this.imageId === null) return true;
    return saveDraft({
      imageId: this.imageId,
      annotatorId,
      boxes: cloneBoxes(this.boxes),
      absent: [...this.absent],
      readingDirection: this.readingDirection,
      readingDirectionAngle: this.readingDirectionAngle,
      layoutType: this.layoutType,
    });
  }

  #cancelDraftTimer(): void {
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    this.#draftTimer = null;
  }

  /**
   * True when there is work in memory that is not known to be on the server.
   *
   * The state machine is the whole answer; box *count* is not part of it. Requiring
   * `boxes.length > 0` meant deleting every box on an image that has server rows and then
   * closing while offline wrote no draft at all — an empty box set is not "no work", it is a
   * deletion of every row the service holds, and the bulk upsert's submitted list is
   * authoritative, so it is exactly as consequential as adding forty boxes.
   */
  get #hasUnsavedWork(): boolean {
    if (this.imageId === null) return false;
    return !this.#settled;
  }

  #scheduleSave(): void {
    if (this.readOnly || this.saveState === 'blocked') return;
    if (this.#saveTimer) clearTimeout(this.#saveTimer);
    this.#saveTimer = setTimeout(() => void this.save(), AUTOSAVE_DELAY_MS);
  }

  #cancelTimers(): void {
    for (const timer of [this.#saveTimer, this.#retryTimer]) {
      if (timer) clearTimeout(timer);
    }
    this.#saveTimer = null;
    this.#retryTimer = null;
  }

  #payload(): BulkUpsertRequest {
    const annotations: AnnotationDraftIn[] = this.boxes.map((box, index) => ({
      id: box.id,
      letter_class: box.letterClass,
      // Floats, straight through. Nothing rounds a coordinate on this path.
      //
      // `angle` is sent as null for an upright box rather than as 0, matching the column: "no
      // rotation" and "rotated by zero" are the same geometry but not the same statement, and
      // the service's CHECK constraint pairs a null angle with an 'aabb' kind.
      bbox: {
        x: box.bbox.x,
        y: box.bbox.y,
        w: box.bbox.w,
        h: box.bbox.h,
        angle: box.bbox.kind === 'aabb' ? null : box.bbox.angle,
        kind: box.bbox.kind,
      },
      alignment: box.alignment,
      expected_token_index: box.alignment === 'expected' ? box.expectedTokenIndex : null,
      word_index: box.wordIndex,
      // The deprecated boolean, still sent because the column is NOT NULL for the length of
      // the expand window. It is restated from `chirality` at `setChirality`, never derived
      // from the reading direction — that was `suggestMirrored`, and it is gone.
      mirrored: box.mirrored,
      // Explicit, including `null`. The bulk list is the image's whole box set, so a key left
      // out of the payload stores NULL — which is the right value for a box nobody has judged,
      // and the wrong one to arrive at by accident. Stating it is what stops this bundle from
      // clearing a judgement it holds in memory.
      chirality: box.chirality,
      uncertain: box.uncertain,
      restored: box.restored,
      reading_order: index,
      notes: box.notes,
      source: box.source,
      // A human box never carries a confidence. The API treats one that does as a provenance
      // violation, and it is right to.
      confidence: box.source === 'human' ? null : box.confidence,
    }));
    const payload: BulkUpsertRequest = {
      // No `annotator_id`. Every box in this payload will be attributed to the authenticated
      // caller, and the request model forbids the field — so a client cannot state whose work
      // a save is, which is the whole point of `data-integrity.md` §4.
      annotations,
      absent_token_indices: [...this.absent].sort((a, b) => a - b),
      mark_in_progress: true,
    };
    if (this.naturalSize) {
      payload.width = Math.round(this.naturalSize.width);
      payload.height = Math.round(this.naturalSize.height);
    }
    // No direction or layout. They belong to the inscription and go through
    // `#saveAttributes`; this payload cannot carry them at all any more. The truthiness
    // guards that used to stand here were also why clearing a direction back to "not set"
    // never reached the service — a `null` simply fell out of the request.
    return payload;
  }

  /**
   * Save, or join the save already running.
   *
   * Returning the outstanding promise rather than `undefined` is what makes a flush mean
   * anything: the old early `return` left `saveState === 'saving'` and looked, to
   * `setStatus()`, exactly like a save that had succeeded — so an image could be marked done
   * and its localStorage draft deleted while its boxes were still only in memory.
   */
  save(): Promise<void> {
    const outstanding = this.#inFlightSave;
    if (outstanding) return outstanding;
    const promise = this.#runSave().finally(() => {
      // Only clear our own: `#reset()` may already have detached this promise and started a
      // new image, and nulling a *newer* save's handle would let a second request start behind
      // it.
      if (this.#inFlightSave === promise) this.#inFlightSave = null;
    });
    this.#inFlightSave = promise;
    return promise;
  }

  /**
   * Drive the save loop to a settled state, or establish that it cannot reach one.
   *
   * Bounded on purpose: a save that keeps coming back retryable is a network problem, and the
   * caller has to be told rather than spun on. On return, `saveState` is authoritative.
   */
  async #flush(attempts = 3): Promise<void> {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (this.#settled) return;
      if (this.saveState === 'failed' || this.saveState === 'blocked') return;
      await this.save();
    }
  }

  get #settled(): boolean {
    return this.saveState === 'clean' || this.saveState === 'saved';
  }

  async #runSave(): Promise<void> {
    if (this.imageId === null || this.readOnly) return;
    if (!annotator.ready) {
      this.saveProblem = asProblem(new Error('set an annotator id before saving'));
      this.saveState = 'failed';
      // The work is still in memory and in the draft; asking is the only thing that unblocks
      // it, and a banner the annotator has already scrolled past is not asking.
      annotator.requestIdentity('save');
      return;
    }
    const generation = this.#generation;
    // Wait out a request left over from a previous session for the same image before issuing
    // another. Guarded so the common path stays synchronous up to `saveState = 'saving'`:
    // `save()` has to be observably "saving" the moment it returns.
    if (this.#draining) {
      const draining = this.#draining;
      await draining;
      if (this.#draining === draining) this.#draining = null;
      if (this.#generation !== generation) return;
      if (this.imageId === null || this.readOnly) return;
    }
    // Both timers exist only to *start* a save; one is starting now, so neither should fire a
    // second one behind it.
    this.#cancelTimers();

    const imageId = this.imageId;
    const sentKeys = this.boxes.map((box) => box.key);
    const sentRevision = this.#revision;
    const payload = this.#payload();

    this.saveState = 'saving';
    try {
      const response = await api.bulkUpsert(imageId, payload);
      // A response for a session that has been torn down must not touch this one: it would
      // stamp another stone's ids, progress and lock state onto the one on screen. Checked
      // against the generation counter, not `imageId` — see `#generation`.
      if (this.#generation !== generation) return;
      this.image = response.image;
      this.rejected = response.rejected;
      this.anomalies = response.anomalies;
      this.retryAttempt = 0;
      this.saveProblem = null;
      this.lastSavedAt = new Date().toISOString();
      // The authoritative set of rows the service now holds. A draft restored later is
      // intersected against this, so a deleted row can never come back as a stale id.
      this.#serverIds = new Set(response.annotations.map((row) => row.id));

      this.#reconcileIds(sentKeys, response.annotations.map((row) => row.id), response.rejected);

      if (this.#revision === sentRevision) {
        this.absent = [...response.absent_token_indices];
        this.saveState = 'saved';
        clearDraft(imageId);
      } else {
        // The annotator kept working while the request was in flight. Their state wins; save
        // again rather than overwriting it with the server's older view.
        this.saveState = 'dirty';
        this.#scheduleSave();
      }
    } catch (error) {
      if (this.#generation !== generation) return;
      const problem = asProblem(error);
      this.saveProblem = problem;
      if (problem.code === 'image_locked') {
        this.#enterLockLoss(problem);
        return;
      }
      if (problem.retryable) {
        this.retryAttempt += 1;
        this.saveState = 'retrying';
        const delay = Math.min(RETRY_BASE_MS * 2 ** (this.retryAttempt - 1), RETRY_MAX_MS);
        if (this.#retryTimer) clearTimeout(this.#retryTimer);
        this.#retryTimer = setTimeout(() => void this.save(), delay);
      } else {
        // Not retryable: the payload itself is the problem. Keep everything, say so, and wait
        // for the annotator — an automatic retry would loop forever on a 422.
        this.saveState = 'failed';
      }
    }
  }

  /**
   * Attach the ids the service assigned.
   *
   * The response lists boxes in `reading_order`, which the write path assigns by submission
   * order over the boxes it *accepted*. Rejected boxes are therefore removed from the sent
   * list before zipping. A count mismatch after that means an assumption broke, so nothing is
   * attached and the next save re-creates rather than mislabelling an existing row.
   */
  #reconcileIds(sentKeys: string[], assignedIds: number[], rejectedBoxes: RejectedBoxOut[]): void {
    let surviving = sentKeys;
    if (rejectedBoxes.length) {
      const rejectedKeys = new Set<string>();
      for (const item of rejectedBoxes) {
        const match = this.boxes.find(
          (box) =>
            box.id === null &&
            box.letterClass === item.letter_class &&
            box.bbox.x === item.bbox.x &&
            box.bbox.y === item.bbox.y &&
            // Two boxes can share an origin at different angles — an annotator working over a
            // ligature does it routinely — and without this the wrong one is dropped.
            box.bbox.angle === (item.bbox.angle ?? 0),
        );
        if (match) rejectedKeys.add(match.key);
      }
      this.boxes = this.boxes.filter((box) => !rejectedKeys.has(box.key));
      surviving = sentKeys.filter((key) => !rejectedKeys.has(key));
    }
    if (surviving.length !== assignedIds.length) return;
    const byKey = new Map(surviving.map((key, index) => [key, assignedIds[index] as number]));
    this.boxes = this.boxes.map((box) =>
      byKey.has(box.key) ? { ...box, id: byKey.get(box.key) ?? box.id } : box,
    );
  }

  /** Retry now, from the banner. */
  async retryNow(): Promise<void> {
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    this.#retryTimer = null;
    await this.save();
  }

  /**
   * Everything unsaved, as JSON, so a stuck annotator can keep their work out of band.
   *
   * `width`/`height` are in here because the boxes are in image pixels and pixels mean nothing
   * without the dimensions they were measured against — a recovery that cannot normalize the
   * numbers is not a recovery. Same reasoning as `images.width`/`images.height` being recorded
   * at annotation time.
   */
  unsavedJson(): string {
    return JSON.stringify(
      {
        image_id: this.imageId,
        siglum: this.image?.siglum ?? null,
        storage_key: this.image?.storage_key ?? null,
        annotator_id: annotator.id,
        exported_at: new Date().toISOString(),
        coordinate_space: 'image pixels, origin top-left, floats; bbox.angle is degrees clockwise about the box centre, and x/y/w/h describe the rectangle at angle zero',
        width: this.naturalSize?.width ?? null,
        height: this.naturalSize?.height ?? null,
        boxes: this.boxes,
        absent_token_indices: this.absent,
        reading_direction: this.readingDirection,
        reading_direction_angle: this.readingDirectionAngle,
        layout_type: this.layoutType,
      },
      null,
      2,
    );
  }

  // ------------------------------------------------------------------------------
  // Status
  // ------------------------------------------------------------------------------

  /**
   * Move the image's status, but only over work that is actually on the server.
   *
   * The old guard was `saveState !== 'failed' && saveState !== 'blocked'`, which passes for
   * `'saving'` — and `save()` used to return immediately while a request was outstanding, so
   * "flush then check" reduced to "check that a save is in progress". The image was marked
   * `done` and `clearDraft()` deleted the localStorage copy of boxes that existed nowhere
   * else. Nothing short of a *settled* state is good enough here, and the draft is only
   * cleared in that state.
   */
  async setStatus(status: ImageStatus, releaseLock: boolean): Promise<boolean> {
    if (this.imageId === null || !annotator.ready) return false;
    // Flush first: the status change must not land ahead of the boxes it describes.
    await this.#flush();
    if (!this.#settled) {
      this.notices = [
        'The status was not changed: this image has work that is not saved yet. The save banner says why.',
      ];
      return false;
    }
    try {
      this.image = await api.setImageStatus(this.imageId, {
        status,
        release_lock: releaseLock,
      });
      // Only now. The draft is the last copy of anything the server does not have.
      if (this.imageId !== null && this.#settled) clearDraft(this.imageId);
      return true;
    } catch (error) {
      const problem = asProblem(error);
      this.saveProblem = problem;
      if (problem.code === 'image_locked') this.#enterLockLoss(problem);
      else this.saveState = 'failed';
      return false;
    }
  }

  /**
   * Drive the save loop to a settled state before leaving this image for another.
   *
   * The same guard `setStatus` uses, and for the same reason: `close()` will write a
   * localStorage draft on the way out, but a draft is a recovery path, not a save. Stepping to
   * the next stone with a failed upsert behind you means the boxes exist only in this browser,
   * and the annotator has no reason to suspect it. So the pager asks here first and refuses on
   * anything short of settled, exactly as complete-and-advance does.
   *
   * `readOnly` returns true rather than refusing. A read-only session (no annotator id, or a
   * lock lost to somebody else) *cannot* reach a settled state by trying harder — refusing
   * would trap the annotator on an image they are not allowed to save — and `close()` plus
   * `#reset()` still protect the work: if the draft cannot be written either, `#reset()` returns
   * false and `open()` refuses to load over the top of it.
   */
  async flushForNavigation(): Promise<boolean> {
    if (this.imageId === null || this.readOnly) return true;
    await this.#flush();
    if (this.#settled) return true;
    this.notices = [
      'You were not moved to another image: this one has work that is not saved yet. The save banner says why.',
    ];
    return false;
  }

  /**
   * Return the open record to the state the corpus delivered it in.
   *
   * Destroys every box and not-present mark on **every** photograph of this stone, each
   * image's status, and the record's reading direction and layout. There is no undo. The
   * confirmation — including the figures and whose boxes they are — is the caller's job, and
   * it must come from `api.resetPreview`, never from what this session happens to hold.
   *
   * The order below is the whole of the difficulty, and none of it is cosmetic:
   *
   * 1. **Quiet the wire first.** A debounced autosave already scheduled, or one in flight,
   *    would land *after* the reset and write every box straight back — the bulk upsert's list
   *    is authoritative, so it would recreate them with new ids. So the timers are cancelled
   *    and the loop is driven to a settled state before the reset is even asked for.
   * 2. **Drop local state without saving it.** `close()` flushes, which is exactly the wrong
   *    move once the rows are gone; clearing `imageId` first makes it a no-op, and `saveState`
   *    is clean so nothing downstream thinks there is work to rescue.
   * 3. **Clear the draft for every image of the record**, not just the open one. A draft is a
   *    copy of the boxes in this browser; leaving one behind means the next visit to a sibling
   *    photograph offers to restore work that was deliberately destroyed.
   * 4. **Reload from the service**, which is now the only copy of the truth. Note what that
   *    implies: `open()` claims the image, so the one on screen comes straight back to
   *    `in_progress` while its siblings stay `unannotated`. That is honest — somebody does have
   *    it open — but it means the reset's own `statuses_reset` figure is a statement about the
   *    moment of the reset, not about the second after it.
   */
  async resetRecord(reason?: string): Promise<InscriptionResetSummary> {
    const record = this.inscription;
    if (!record) throw new Error('no record is open, so there is nothing to reset');
    const imageId = this.imageId;

    // (1) Nothing of this session's may reach the service after the reset does.
    this.#cancelTimers();
    this.#cancelDraftTimer();
    if (!this.readOnly && imageId !== null) await this.#flush();

    const result = await api.resetInscription(record.id, {
      confirm: true,
      ...(reason ? { reason } : {}),
    });

    // (2) The boxes in memory describe rows that no longer exist.
    this.boxes = [];
    this.absent = [];
    this.#serverIds = new Set();
    this.saveState = 'clean';
    this.saveProblem = null;
    this.imageId = null;

    // (3) Including the siblings, which this session never had open.
    for (const id of result.cleared.image_ids) clearDraft(id);
    if (imageId !== null) clearDraft(imageId);

    // (4) `#openedId` is what stops `open()` re-entering for the image it already has; the
    // record has changed underneath it, so it is no longer the same image.
    this.#openedId = null;
    if (imageId !== null) await this.open(imageId);
    return result.cleared;
  }

  markComplete(): Promise<boolean> {
    return this.setStatus('done', true);
  }

  skip(): Promise<boolean> {
    return this.setStatus('skipped', true);
  }

  /**
   * Record that this photograph does not show this inscription.
   *
   * Distinct from `skip()` on purpose. Skipping says "I cannot work this one" and leaves the
   * question open; this says the question is answered by looking — the frame is a panel shot of
   * a neighbouring text on the same rock, and this record's letters are not in it. On this
   * corpus that is ordinary rather than exceptional: 68% of image rows are byte-identical to a
   * file filed under a different siglum. See docs/panel_photographs.md.
   *
   * Boxes already drawn are **not** cleared. If somebody boxed letters here they saw something,
   * and silently deleting that on a status change would destroy training data from a button
   * that does not say it will. Clearing the record is `resetRecord()`, which asks first.
   */
  markNotPictured(): Promise<boolean> {
    return this.setStatus('not_pictured', true);
  }

  /** Unresolved chips, for the confirmation prompt before completing an image. */
  get unresolvedTokens(): TokenOut[] {
    return this.tokens.filter((token) => !this.resolved.has(token.index));
  }

  get lacunaTokens(): TokenOut[] {
    return this.tokens.filter((token) => isLacunaToken(token));
  }

  // ------------------------------------------------------------------------------
  // Teardown
  // ------------------------------------------------------------------------------

  async close(): Promise<void> {
    this.#cancelTimers();
    const imageId = this.imageId;
    if (imageId === null) {
      this.#cancelDraftTimer();
      return;
    }
    // Persist the draft *before* cancelling its debounce. Cancelling first threw away up to
    // DRAFT_DELAY_MS of boxes, and `#reset()` was about to empty the only other copy — so a
    // failing final save meant the last few boxes existed nowhere at all.
    if (this.#hasUnsavedWork) this.#writeDraft();
    this.#cancelDraftTimer();

    // `!#settled` rather than an enumeration of dirty/retrying. `'saving'` was excluded, so
    // `close()` returned with a request in the air: `#settled` was then false, the lock was
    // *not* released, the response was discarded by the generation guard, and the draft written
    // above carried `id: null` for boxes the service had in fact just stored. Reopening told
    // the annotator those boxes had never reached the service — untrue — and restoring re-sent
    // them with null ids, so the authoritative upsert deleted and re-created the rows with new
    // ids and new `created_at`. Provenance churn for nothing.
    if (!this.readOnly && !this.#settled) {
      await this.#flush();
      // The flush may have taken longer than the debounce would have; re-persist whatever is
      // still outstanding so the on-disk copy matches memory.
      if (this.#hasUnsavedWork) this.#writeDraft();
    }
    // Release the lock on the way out so the next annotator does not wait out the TTL. Only
    // when there is nothing outstanding: an image with unsaved work keeps its claim, because
    // the retry loop is still going to need it.
    const settled = this.#settled;
    if (!this.readOnly && this.image && annotator.ready && settled) {
      try {
        await api.setImageStatus(imageId, {
          status: this.image.status,
          release_lock: true,
        });
      } catch {
        // Releasing is a courtesy. Failing to do it costs a TTL, not any work.
      }
    }
  }

  /**
   * Clear the session for a new image.
   *
   * Returns `false` — and clears nothing — when there is unsaved work that could not be
   * written to localStorage either. That is the only situation in which emptying `boxes`
   * destroys the last copy of an annotator's work, and the caller refuses to open over the
   * top of it rather than losing it politely.
   *
   * The commoner case (unsaved work, storage available) is not a refusal: the draft is written
   * synchronously and offered again the next time the image is opened. Refusing there would
   * mean the previous image's boxes stayed attached to a newly opened image, which is a worse
   * kind of data corruption than a stale draft.
   */
  #reset(): boolean {
    if (this.#hasUnsavedWork && !this.#writeDraft()) {
      this.strandedWork = true;
      return false;
    }
    this.strandedWork = false;
    this.#cancelTimers();
    this.#cancelDraftTimer();
    // A new session. Everything issued by the old one is now stale, whatever image it was for.
    this.#generation += 1;
    // Detach any request still in the air: it belongs to the session being torn down, its
    // result is discarded by the generation guard in `#runSave`, and a new session must not be
    // able to join it and mistake it for its own save.
    //
    // Detaching is not the same as forgetting. It is kept as `#draining` so the next save waits
    // for it rather than racing it: navigating A -> B -> A inside one in-flight request would
    // otherwise put two bulk upserts for image A on the wire at once, and the submitted list is
    // authoritative, so the older one landing second deletes every box the newer one added.
    if (this.#inFlightSave) {
      const outstanding = this.#inFlightSave.catch(() => undefined);
      const previous = this.#draining;
      // Chained, never overwritten: a third session must wait for both of the first two. An
      // assignment here would drop the earlier request on the floor and reintroduce the race.
      const chained = previous ? previous.then(() => outstanding) : outstanding;
      this.#draining = chained;
      // Self-clearing. Once it has landed there is nothing left to wait for, and a handle kept
      // past that point would make every subsequent save pay a needless turn of the event loop.
      void chained.then(() => {
        if (this.#draining === chained) this.#draining = null;
      });
    }
    this.#inFlightSave = null;
    this.#serverIds = new Set();
    this.imageId = null;
    this.image = null;
    this.inscription = null;
    this.imageUrl = null;
    this.imageProblem = null;
    this.loadProblem = null;
    this.boxes = [];
    this.absent = [];
    this.cursor = 0;
    this.selectedKey = null;
    this.readingDirection = null;
    this.readingDirectionAngle = null;
    this.layoutType = null;
    this.attributeSaving = false;
    this.attributeProblem = null;
    this.#attributePending = 0;
    this.naturalSize = null;
    this.saveState = 'clean';
    this.saveProblem = null;
    this.lockLoss = null;
    this.readOnly = false;
    this.retryAttempt = 0;
    this.rejected = [];
    this.anomalies = [];
    this.notices = [];
    this.recoverable = null;
    this.recoverableOwner = 'unattributed';
    this.recoverableStale = false;
    this.insertMode = false;
    this.#undo = [];
    this.#redo = [];
    this.#revision = 0;
    return true;
  }
}

export const session = new EditorSession();
